import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/hoobuyData';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50 border-b border-background-200">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="text-lg md:text-xl font-extrabold text-foreground-950 whitespace-nowrap">
              hoobuy<span className="text-primary-500">.motorcycles</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <Link to="/" className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              Início
            </Link>
            <Link to="/#artigos" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              Guia de Compras
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Hoobuy Sheet
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-background-300 bg-background-50 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-foreground-950 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line text-base"></i>
                <span>Categorias</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-xl border border-background-200 overflow-hidden max-h-96 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-9 h-9 rounded-md overflow-hidden shrink-0 bg-background-100">
                        <img src={cat.image} alt={cat.namePt} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium">{cat.namePt}</span>
                        <span className="text-[10px] text-foreground-400 truncate">{cat.description}</span>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-900 hover:bg-background-100 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[75vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line text-base"></i>
              </span>
              Início
            </Link>
            <Link
              to="/#artigos"
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-base"></i>
              </span>
              Guia de Compras
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-file-list-3-line text-base"></i>
              </span>
              Hoobuy Sheet
            </a>

            <button
              onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-grid-line text-base"></i>
                </span>
                Categorias
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCategoryOpen && (
              <div className="grid grid-cols-2 gap-2 px-2 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-primary-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-md overflow-hidden shrink-0 bg-background-100">
                      <img src={cat.image} alt={cat.namePt} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.namePt}</span>
                  </a>
                ))}
              </div>
            )}

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 mt-3 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold"
            >
              Buy It Now
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}